import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Icon } from "@/components/Icon";
import { DevaAvatar } from "@/components/Deva";
import { useRealtime, type RealtimeEvent } from "@/lib/realtime";
import { isMobilePath, MOBILE_BASE } from "@/mobile/device";
import { reminderChatLink } from "@/pages/Chats/reminders";
import {
  addOne,
  addSummary,
  COALESCE_MS,
  HIDE_MS,
  planBurst,
  removeToast,
  stageSignal,
  stageToastView,
  summaryTitle,
  type StageSignal,
  type StageToast,
} from "./stageToasts";
import "./stageToast.css";

// Смена этапа или статуса кандидатуры в МОЁМ чате (я — владелец,
// Chat.recruiter_id), сделанная не мной: коллегой, автоматикой, Девой.
// Всплывашка «Повар · Иван Петров: Встреча назначена → Встреча проведена ·
// автоматически», нажатие открывает чат этого человека. Только внутри HRDeva:
// в Telegram/MAX такое не уходит (решение Данила). В самом чате та же смена —
// тихой строкой ленты (chat-service stage_feed), она остаётся и без всплывашки.
//
// Хост на любой странице — рядом с напоминаниями (AppShell полной версии и
// оболочка MobileApp). Кого и как показывать — stageToasts.ts: SSE `stage`
// несёт одни id, пачка событий копится COALESCE_MS и становится сводной
// «Изменён этап у N кандидатов», если кандидатур много.
//
// Снимка «что я пропустил» нет, и за разрыв потока всплывашки не
// восстанавливаются: это новость, а не дело (как напоминание) — строки в
// чатах остаются.
//
// Место — левый нижний угол полной версии, над колонкой списка чатов: верх
// справа занят звонками и напоминаниями (напоминание висит, пока его не
// закроют, и накрыло бы всплывашку этапа), низ справа — трей загрузок и
// «Отправить». На телефоне — сверху, как там принято, слоем ниже напоминаний:
// внизу переписки поле ввода.

export function StageToastHost() {
  const { state: auth } = useAuth();
  const meRef = useRef<string | null>(null);
  meRef.current = auth.status === "authed" ? auth.user.id : null;
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = isMobilePath(location.pathname);

  const [items, setItems] = useState<StageToast[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const pending = useRef<StageSignal[]>([]);
  const seen = useRef<Set<string>>(new Set());
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const flush = useCallback(() => {
    const summaryShown = itemsRef.current.some((t) => t.kind === "summary");
    const plan = planBurst(pending.current, seen.current, summaryShown);
    pending.current = [];
    if (!plan) return;
    for (const id of plan.ids) seen.current.add(id);
    if (plan.summary.length) {
      setItems((prev) => addSummary(prev, plan.summary, `sum:${plan.ids[0]}`));
      return;
    }
    for (const id of plan.fetch) {
      api.stageEvent(id).then(
        (e) => setItems((prev) => addOne(prev, stageToastView(e))),
        // Старый chat-service без ручки, событие удалено вместе с кандидатурой
        // или нет сети — просто без всплывашки: строка в чате есть и так.
        () => undefined,
      );
    }
  }, []);

  const onEvent = (ev: RealtimeEvent) => {
    const sig = stageSignal(ev, meRef.current);
    if (!sig) return;
    pending.current.push(sig);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, COALESCE_MS);
  };
  useRealtime(onEvent);

  const hide = useCallback((key: string) => setItems((prev) => removeToast(prev, key)), []);
  const open = (t: StageToast) => {
    hide(t.key);
    // Одна — чат этого человека (как у напоминания: `?person=` полной версии,
    // слой `?chat=` телефона); сводная — список чатов.
    const to =
      t.kind === "one"
        ? t.personId && reminderChatLink(t.personId, mobile, location.search)
        : mobile
          ? MOBILE_BASE
          : "/chats";
    if (to) navigate(to);
  };

  // Контейнер в DOM всегда, даже пустой: живую область, вставленную вместе с
  // содержимым, дикторы часто не объявляют.
  return (
    <div className="stoast-stack" role="region" aria-label="Смены этапов" aria-live="polite">
      {items.map((t) => (
        <StageToastCard key={t.key} toast={t} onOpen={() => open(t)} onHide={() => hide(t.key)} />
      ))}
    </div>
  );
}

export function StageToastCard({
  toast: t,
  onOpen,
  onHide,
}: {
  toast: StageToast;
  onOpen: () => void;
  onHide: () => void;
}) {
  // Уходит сама через HIDE_MS; пока над ней мышь или в ней фокус — ждёт.
  // Сводная, к которой прибавилось (`rev`), заводит срок заново.
  const [held, setHeld] = useState(false);
  const rev = t.kind === "summary" ? t.rev : 0;
  const hideRef = useRef(onHide);
  hideRef.current = onHide;
  useEffect(() => {
    if (held) return;
    const id = window.setTimeout(() => hideRef.current(), HIDE_MS);
    return () => window.clearTimeout(id);
  }, [held, rev]);

  const summary = t.kind === "summary";
  const title = summary ? summaryTitle(t.candidates.length) : t.title;
  return (
    <section
      className={"stoast" + (summary ? " stoast--sum" : "")}
      aria-label={summary ? title : t.aria}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHeld(false);
      }}
    >
      <button
        type="button"
        className="stoast-main"
        onClick={onOpen}
        title={summary ? "Открыть чаты" : "Открыть чат"}
      >
        {!summary && t.deva ? (
          // Сменила Дева — её аватарка, а не значок этапа.
          <span className="stoast-av" aria-hidden="true">
            <DevaAvatar size={32} />
          </span>
        ) : (
          <span className="stoast-ic" aria-hidden="true">
            <Icon name={!summary && t.statusOnly ? "flag" : "stage"} size={18} strokeWidth={1.8} />
          </span>
        )}
        <span className="stoast-text">
          {!summary && <span className="stoast-eyebrow">{t.eyebrow}</span>}
          <span className="stoast-title">{title}</span>
          {summary ? (
            <span className="stoast-by">Подробности — строками в их чатах</span>
          ) : (
            <>
              <span className="stoast-change">{t.change}</span>
              <span className="stoast-by">{t.by}</span>
            </>
          )}
        </span>
      </button>
      <button type="button" className="stoast-x" onClick={onHide} aria-label="Скрыть" title="Скрыть">
        <Icon name="close" size={14} />
      </button>
    </section>
  );
}
